"use client";

import {
	citedVsNot,
	coverageCitationHit,
	coverageGroup,
	coverageImportLines,
	coverageImportResult,
	coverageRows,
	projectGoals,
} from "@marketing/shared/lib/bento-fixtures";
import { AipvsTierBadge } from "@saas/publisher-lookup/components/AipvsTierBadge";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import { CHART_SERIES } from "@ui/lib/chart-palette";
import { ArrowDownIcon, CheckIcon, LinkIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { CoverageFunnelTile } from "./DataTiles";
import { ScoredTier } from "./MediaListTiles";
import { useRevealed } from "./motion";

/**
 * Coverage Reports marketing tiles (Coverage Groups in the app), on the
 * "Spring launch coverage" fixture (32 URLs → 14 cited → 6 cited inline, 9%
 * influence). The funnel reuses CoverageFunnelTile; everything else mirrors
 * the group detail page, the AI citations page and a PR project's goal row.
 * Chrome and prose read from `marketing.coverageReport.widgets`.
 */

const NS = "marketing.coverageReport.widgets";

const strong = (chunks: ReactNode) => (
	<span className="font-semibold text-foreground">{chunks}</span>
);

/** "Spring launch coverage · 32 URLs": the header every coverage tile uses. */
export function CoverageGroupHeader({ className }: { className?: string }) {
	const t = useTranslations(NS);
	return (
		<p
			className={cn(
				"text-xs font-semibold text-muted-foreground",
				className,
			)}
		>
			{t("groupHeader", {
				name: t("groupName"),
				count: coverageGroup.urls,
			})}
		</p>
	);
}

/** The influence line a project shows for an attached group. */
export function InfluenceLine({ className }: { className?: string }) {
	const t = useTranslations(NS);
	return (
		<p
			className={cn(
				"rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground",
				className,
			)}
		>
			{t.rich("influenceLine", {
				name: t("groupName"),
				urls: coverageGroup.urls,
				citations: coverageGroup.citations,
				pct: coverageGroup.influencePct,
				strong,
			})}
		</p>
	);
}

/** Hero: the reused funnel plus the influence line. */
export function CoverageHeroTile({ className }: { className?: string }) {
	return (
		<div className={cn("space-y-4", className)}>
			<CoverageGroupHeader />
			<CoverageFunnelTile />
			<InfluenceLine />
		</div>
	);
}

// ── Import ──────────────────────────────────────────────────────────────────

export function CoveragePasteTile({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const revealed = useRevealed();
	return (
		<div className={cn("space-y-3", className)}>
			<div className="rounded-xl border bg-background">
				<p className="border-b px-3 py-2 text-[11px] font-semibold text-muted-foreground">
					{t("pasteHeader")}
				</p>
				<ul className="space-y-1 px-3 py-2.5 font-mono text-[11px] leading-relaxed text-muted-foreground">
					{coverageImportLines.map((line) => (
						<li key={line.raw} className="truncate">
							{line.raw}
						</li>
					))}
					<li className="text-muted-foreground/60">
						{t("moreLines", {
							count: coverageImportResult.morePasted,
						})}
					</li>
				</ul>
			</div>

			<div className="flex justify-center" aria-hidden>
				<ArrowDownIcon className="size-4 text-muted-foreground" />
			</div>

			<ul className="divide-y rounded-xl border bg-background">
				{coverageImportLines.map((line, i) => (
					<li
						key={line.clean}
						className={cn(
							"flex items-start gap-2.5 px-3 py-2 motion-safe:transition-opacity motion-safe:duration-500",
							revealed
								? "motion-safe:opacity-100"
								: "motion-safe:opacity-0",
						)}
						style={{ transitionDelay: `${200 + i * 120}ms` }}
					>
						<CheckIcon
							className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
							aria-hidden
						/>
						<div className="min-w-0">
							<p className="truncate text-xs font-medium text-foreground">
								{line.clean}
							</p>
							<p className="text-[11px] text-muted-foreground">
								{t(`importNotes.${line.note}`)}
							</p>
						</div>
					</li>
				))}
			</ul>

			<p className="text-xs text-muted-foreground">
				<span className="font-semibold text-foreground">
					{t("urlsAdded", { count: coverageImportResult.added })}
				</span>
			</p>
		</div>
	);
}

// ── Score ───────────────────────────────────────────────────────────────────

export function CoverageScoreRows({ className }: { className?: string }) {
	const t = useTranslations(NS);
	return (
		<div className={cn("space-y-3", className)}>
			<div className="flex items-baseline justify-between gap-2">
				<CoverageGroupHeader />
			</div>
			<ul className="divide-y rounded-xl border bg-background">
				{coverageRows.map((row) => (
					<li key={row.url} className="px-3.5 py-3">
						<div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
							<div className="min-w-0 flex-1 basis-56">
								<p className="truncate text-sm font-medium">
									{t(`articles.${row.title}`)}
								</p>
								<p className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
									<LinkIcon
										className="size-3 shrink-0"
										aria-hidden
									/>
									<span className="truncate">{row.url}</span>
								</p>
							</div>
							<span className="inline-flex shrink-0 items-center gap-1.5">
								<span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
									{t("aipqs")}
								</span>
								<ScoredTier
									score={row.aipqs.score}
									tier={row.aipqs.tier}
								/>
							</span>
						</div>
						<div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
							<span className="flex flex-wrap gap-1.5">
								<TypeChip>
									{t(`mediaTypes.${row.mediaType}`)}
								</TypeChip>
								<TypeChip>
									{t(`formats.${row.format}`)}
								</TypeChip>
							</span>
							<span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
								{t("publisherAipvs")}
								<span className="font-mono font-semibold tabular-nums text-foreground">
									{row.aipvs.score}
								</span>
								<AipvsTierBadge tier={row.aipvs.tier} />
							</span>
						</div>
					</li>
				))}
			</ul>
			<p className="text-[11px] text-muted-foreground">
				{t("showingRows", {
					shown: coverageGroup.shownRows,
					total: coverageGroup.urls,
				})}
			</p>
		</div>
	);
}

/** Classification label. Neutral outline: a type, not a verdict. */
function TypeChip({ children }: { children: string }) {
	return (
		<span className="inline-flex items-center rounded-md border px-1.5 py-px text-[11px] font-medium text-muted-foreground">
			{children}
		</span>
	);
}

// ── Influence ───────────────────────────────────────────────────────────────

export function InfluenceGoalTile({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const revealed = useRevealed();
	const max = Math.max(...projectGoals.map((g) => g.pct), 1);
	return (
		<div className={cn("space-y-3", className)}>
			<div className="flex items-baseline justify-between gap-2">
				<p className="text-xs font-semibold text-muted-foreground">
					{t("projectEyebrow", { project: t("projectName") })}
				</p>
				<p className="text-[11px] text-muted-foreground">
					{t("goals")}
				</p>
			</div>
			<ul className="space-y-2">
				{projectGoals.map((goal, i) => {
					const focus = i === 0;
					const label =
						goal.label ??
						t("coverageGoalLabel", {
							name: t("groupName"),
							count: coverageGroup.urls,
						});
					return (
						<li
							key={goal.kind}
							className={cn(
								"rounded-xl border px-3.5 py-3",
								focus ? "bg-background" : "bg-background/40",
							)}
						>
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
										{t(`goalKinds.${goal.kind}`)}
									</p>
									<p
										className={cn(
											"text-sm",
											focus
												? "font-semibold text-foreground"
												: "text-muted-foreground",
										)}
									>
										{label}
									</p>
								</div>
								<div className="shrink-0 text-right">
									<p
										className={cn(
											"font-mono font-extrabold tabular-nums",
											focus
												? "text-2xl text-foreground"
												: "text-base text-muted-foreground",
										)}
									>
										{goal.pct}%
									</p>
									<p className="text-[11px] text-muted-foreground">
										{t("citationsCount", {
											count: goal.citations,
										})}
									</p>
								</div>
							</div>
							<div className="mt-2 h-1.5 rounded-full bg-muted">
								<div
									className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
									style={{
										width: revealed
											? `${(goal.pct / max) * 100}%`
											: "0%",
										backgroundColor: focus
											? CHART_SERIES[0]
											: "var(--muted-foreground)",
										opacity: focus ? 1 : 0.35,
										transitionDelay: `${i * 140}ms`,
									}}
								/>
							</div>
							{focus ? (
								<div className="mt-2 flex flex-wrap items-center gap-2">
									<Badge
										status="success"
										className="px-2 py-0.5 normal-case"
									>
										{t("hit")}
									</Badge>
									<span className="text-[11px] text-muted-foreground">
										{t("pastCounted")}
									</span>
								</div>
							) : null}
						</li>
					);
				})}
			</ul>
			<p className="text-[11px] text-muted-foreground">
				{t("influenceFootnote", {
					count: coverageGroup.projectCitations,
				})}
			</p>
		</div>
	);
}

// ── Proof ───────────────────────────────────────────────────────────────────

export function CitationHitCard({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const hit = coverageCitationHit;
	return (
		<div className={cn("space-y-3", className)}>
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="inline-flex items-center gap-1.5">
					{/* eslint-disable-next-line @next/next/no-img-element */}
					<img
						src={hit.platformLogo}
						alt=""
						className="size-4 rounded-[3px] bg-white object-contain p-px"
						aria-hidden
					/>
					<span className="text-xs font-semibold">
						{hit.platform}
					</span>
					<span className="text-[11px] text-muted-foreground">
						· {t("hitDate")}
					</span>
				</span>
				<span className="inline-flex items-center rounded-full border px-2 py-px text-[11px] font-semibold text-foreground">
					{t("hitType")}
				</span>
			</div>
			<div>
				<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
					{t("promptLabel")}
				</p>
				<p className="mt-0.5 text-sm font-medium text-foreground">
					{t("promptText")}
				</p>
			</div>
			<blockquote className="rounded-lg border-l-2 border-primary bg-muted/50 px-3 py-2.5 text-sm leading-relaxed text-foreground">
				{t.rich("hitSentence", {
					mark: (chunks) => (
						<mark className="rounded bg-primary/15 px-0.5 text-foreground">
							{chunks}
						</mark>
					),
				})}
			</blockquote>
			<p className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
				<LinkIcon className="size-3 shrink-0" aria-hidden />
				<span className="truncate">{hit.url}</span>
			</p>
		</div>
	);
}

/** Cited vs not-cited: neutral marks, the brief for the next round. */
export function CitedVsNotTile({ className }: { className?: string }) {
	const t = useTranslations(NS);
	const revealed = useRevealed();
	const groups = [
		{
			key: "cited" as const,
			label: t("citedLegend", { count: citedVsNot.cited }),
			color: CHART_SERIES[0],
			opacity: 1,
		},
		{
			key: "notCited" as const,
			label: t("notCitedLegend", { count: citedVsNot.notCited }),
			color: "var(--muted-foreground)",
			opacity: 0.35,
		},
	];
	return (
		<div className={cn("space-y-4", className)}>
			<div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
				{groups.map((g) => (
					<span key={g.key} className="flex items-center gap-1.5">
						<span
							className="size-2 rounded-full"
							style={{
								backgroundColor: g.color,
								opacity: g.opacity,
							}}
						/>
						{g.label}
					</span>
				))}
			</div>
			{citedVsNot.rows.map((row, i) => (
				<div key={row.key}>
					<p className="text-xs font-medium">
						{t(`compareRows.${row.key}`)}
					</p>
					<div className="mt-1.5 space-y-1">
						{groups.map((g) => {
							const value = row[g.key];
							return (
								<div
									key={g.key}
									className="flex items-center gap-2"
								>
									<div className="h-2 flex-1 rounded-full bg-muted">
										<div
											className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
											style={{
												width: revealed
													? `${(value / row.max) * 100}%`
													: "0%",
												backgroundColor: g.color,
												opacity: g.opacity,
												transitionDelay: `${i * 140}ms`,
											}}
										/>
									</div>
									<span className="w-12 shrink-0 text-right font-mono text-xs font-semibold tabular-nums">
										{row.format(value)}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);
}
